/* Le Matin — gelato flavour multi-select.
   Enforces a max number of flavours based on the selected Tub Size and writes
   the chosen flavours into the hidden `properties[Flavours]` input. Requires at
   least one flavour before the product can be added to cart. */
(function () {
  function initAccordion(details) {
    if (!details || details.dataset.gelatoAccordionReady) return;
    details.dataset.gelatoAccordionReady = '1';

    const content = details.querySelector('.accordion__content');
    const summary = details.querySelector('summary');
    if (!content || !summary) return;

    const DURATION = 500;
    const ACCORDION_EVENT = 'lematin:accordion-open';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let animating = false;

    const clear = () => {
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
      content.style.opacity = '';
      content.style.paddingTop = '';
      content.style.paddingBottom = '';
      animating = false;
    };

    /* Vertical padding has to be animated alongside the height. With
       box-sizing:border-box a `height:0` element still renders at least as tall
       as its padding, so leaving the padding in place makes the separator snap by
       the padding amount at the very start/end of the animation. */
    const readPadding = () => {
      const cs = getComputedStyle(content);
      return { top: cs.paddingTop, bottom: cs.paddingBottom };
    };

    const transition = () =>
      'height ' + DURATION + 'ms ease-in-out, ' +
      'padding-top ' + DURATION + 'ms ease-in-out, ' +
      'padding-bottom ' + DURATION + 'ms ease-in-out, ' +
      'opacity ' + DURATION + 'ms ease-in-out';

    /* Broadcast so the other product-page accordions collapse — only one open
       at a time. */
    const announceOpen = () => {
      document.dispatchEvent(new CustomEvent(ACCORDION_EVENT, { detail: { source: details } }));
    };

    const expand = () => {
      announceOpen();
      animating = true;
      details.open = true;
      const pad = readPadding();
      const target = content.scrollHeight;
      content.style.overflow = 'hidden';
      content.style.height = '0px';
      content.style.paddingTop = '0px';
      content.style.paddingBottom = '0px';
      content.style.opacity = '0';
      requestAnimationFrame(() => {
        content.style.transition = transition();
        content.style.height = target + 'px';
        content.style.paddingTop = pad.top;
        content.style.paddingBottom = pad.bottom;
        content.style.opacity = '1';
      });
      const done = (e) => {
        if (e.propertyName !== 'height') return;
        content.removeEventListener('transitionend', done);
        clear();
      };
      content.addEventListener('transitionend', done);
    };

    const collapse = () => {
      animating = true;
      content.style.overflow = 'hidden';
      content.style.height = content.scrollHeight + 'px';
      content.style.opacity = '1';
      requestAnimationFrame(() => {
        content.style.transition = transition();
        content.style.height = '0px';
        content.style.paddingTop = '0px';
        content.style.paddingBottom = '0px';
        content.style.opacity = '0';
      });
      const done = (e) => {
        if (e.propertyName !== 'height') return;
        content.removeEventListener('transitionend', done);
        details.open = false;
        clear();
      };
      content.addEventListener('transitionend', done);
    };

    summary.addEventListener('click', (e) => {
      if (reduceMotion) {
        if (!details.open) announceOpen();
        return;
      }
      e.preventDefault();
      if (animating) return;
      if (details.open) collapse();
      else expand();
    });

    document.addEventListener(ACCORDION_EVENT, (e) => {
      if (e.detail.source === details || !details.open) return;
      if (reduceMotion) { details.open = false; return; }
      if (animating) return;
      collapse();
    });
  }

  function variantSelectsFor(root) {
    const prev = root.previousElementSibling;
    if (prev && prev.tagName === 'VARIANT-SELECTS') return prev;
    return (
      root.closest('product-info')?.querySelector('variant-selects') ||
      root.parentElement?.querySelector('variant-selects')
    );
  }

  function readSizeFromVariantSelects(vs) {
    if (!vs) return '';

    const checkedRadio = vs.querySelector(
      '.product-form__input--pill input[type="radio"]:checked, fieldset input[type="radio"]:checked'
    );
    if (checkedRadio) return String(checkedRadio.value).trim();

    const select = vs.querySelector('select.select__select');
    if (select) return String(select.value).trim();

    const jsonEl = vs.querySelector('[data-selected-variant]');
    if (jsonEl) {
      try {
        const variant = JSON.parse(jsonEl.textContent);
        if (variant.option1) return String(variant.option1).trim();
        if (variant.title) return String(variant.title).split(' / ')[0].trim();
      } catch (e) { /* ignore */ }
    }

    return '';
  }

  function maxForSize(val, maxMap, defaultMax) {
    if (!val) return defaultMax;

    if (Object.prototype.hasOwnProperty.call(maxMap, val)) return maxMap[val];

    const normalized = val.toLowerCase().replace(/\s+/g, '');
    if (/500/.test(normalized)) return 3;
    if (/1000|1l/.test(normalized)) return 4;

    for (const key of Object.keys(maxMap)) {
      const keyNorm = key.toLowerCase().replace(/\s+/g, '');
      if (normalized === keyNorm || normalized.includes(keyNorm)) return maxMap[key];
    }

    return defaultMax;
  }

  function init(root) {
    if (root.dataset.gelatoReady) return;
    root.dataset.gelatoReady = '1';

    const checkboxes = Array.from(root.querySelectorAll('[data-gelato-checkbox]'));
    const hidden = root.querySelector('[data-gelato-input]');
    const counter = root.querySelector('[data-gelato-counter]');
    if (!checkboxes.length || !hidden) return;

    let maxMap = {};
    try { maxMap = JSON.parse(root.dataset.maxMap || '{}'); } catch (e) { maxMap = {}; }
    const defaultMax = parseInt(root.dataset.defaultMax, 10) || 3;

    const formId = hidden.getAttribute('form');
    const form = formId ? document.getElementById(formId) : root.closest('form');
    const productInfo = root.closest('product-info');

    function addButton() {
      return form ? form.querySelector('[name="add"]') : null;
    }

    function currentMax() {
      return maxForSize(readSizeFromVariantSelects(variantSelectsFor(root)), maxMap, defaultMax);
    }

    function selected() {
      return checkboxes.filter((c) => c.checked);
    }

    function update() {
      const max = currentMax();
      const chosen = selected();

      if (chosen.length > max) {
        chosen.slice(max).forEach((c) => { c.checked = false; });
      }
      const finalChosen = selected();

      checkboxes.forEach((c) => {
        c.disabled = !c.checked && finalChosen.length >= max;
        c.closest('.gelato-flavours__item').classList.toggle('is-disabled', c.disabled);
      });

      hidden.value = finalChosen.map((c) => c.value).join(', ');

      if (counter) {
        counter.textContent = finalChosen.length + ' / ' + max + ' selected';
      }

      const btn = addButton();
      if (!btn) return;

      if (finalChosen.length < 1) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.gelatoBlocked = '1';
      } else if (btn.dataset.gelatoBlocked) {
        btn.removeAttribute('disabled');
        delete btn.dataset.gelatoBlocked;
      }
    }

    checkboxes.forEach((c) => c.addEventListener('change', update));

    // Listen on product-info so tub-size changes still apply after Dawn re-renders
    // variant-selects via AJAX (the old variant-selects node is replaced).
    if (productInfo) {
      productInfo.addEventListener('change', (e) => {
        const vs = variantSelectsFor(root);
        if (!vs || !vs.contains(e.target)) return;
        if (e.target.matches('input[type="radio"], select')) update();
      });
    }

    update();
  }

  function boot() {
    document.querySelectorAll('.gelato-options-accordion details').forEach(initAccordion);
    document.querySelectorAll('[data-gelato-flavours]').forEach(init);
  }

  function resetAndBoot() {
    document.querySelectorAll('[data-gelato-flavours]').forEach((root) => {
      delete root.dataset.gelatoReady;
    });
    boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('product-info:loaded', resetAndBoot);
})();
